import { AccessLevel, ExtranetRole, PageArea } from '../models/extranet.model';

/** A tier-2 tab. `label` is an i18n key (the shell applies `| translate`). */
export interface NavItem {
  label: string;
  icon: string;
  route: string;
  /** Submenu rows — a popover on desktop, indented rows in the mobile drawer (handoff §2). */
  children?: NavChild[];
  /** The page area the tab opens (Feature 4123); none = shown to every member (Settings, admin). */
  area?: PageArea;
}

export interface NavChild {
  label: string;
  icon?: string;
  route: string;
  /** Active only on this exact path (default: on the path and everything under it). */
  exact?: boolean;
  /** The page area of the row's page (Feature 4123): a report row is REPORTS, whatever its parent. */
  area?: PageArea;
}

const HOME: NavItem = { label: 'nav.home', icon: 'home', route: '/extranet/dashboard', area: 'HOME' };
const RATES: NavItem = { label: 'nav.ratesAvailability', icon: 'date_range', route: '/extranet/calendar', area: 'RATES' };
const PROPERTIES: NavItem = { label: 'nav.properties', icon: 'hotel', route: '/extranet/properties', area: 'PROPERTIES' };
const SETTINGS: NavItem = { label: 'nav.settings', icon: 'settings', route: '/extranet/settings' };

// Reservations is a group for both manager roles (WI 4130): its Dashboard row comes first, then the list.
// The parent keeps the list's route, so its label still opens the list and the tab stays lit on the
// whole /extranet/bookings subtree (dashboard and a booking's detail included); the rows light up on
// their own page only.
const RESERVATIONS_DASHBOARD: NavChild = { label: 'nav.children.reservationsDashboard', icon: 'space_dashboard', route: '/extranet/bookings/dashboard', area: 'RESERVATIONS' };
const ALL_RESERVATIONS: NavChild = { label: 'nav.children.allReservations', icon: 'event_available', route: '/extranet/bookings', exact: true, area: 'RESERVATIONS' };

// Notifications is deliberately NOT a nav tab — matches the prototype's Shell.jsx NAV array. The
// notifications route is reachable only via the bell dropdown's "View all notifications" link.
export const HOTEL_NAV: NavItem[] = [
  HOME,
  RATES,
  {
    label: 'nav.reservations', icon: 'event_available', route: '/extranet/bookings', area: 'RESERVATIONS',
    children: [RESERVATIONS_DASHBOARD, ALL_RESERVATIONS],
  },
  PROPERTIES,
  SETTINGS,
];

/**
 * Agency tabs in the handoff's order (CLAUDE-CODE-HANDOFF §2): Home · Rates & Availability ·
 * Reservations · Clients · Suppliers · Contracts · Banking. Reporting is nested under the thing
 * it reports ON (a flat "Reports" tab would collect unrelated screens under one label), so four
 * tabs carry a submenu with an "All …" row on the parent's own route (exact match). That row comes
 * first, except under Reservations, which opens with its Dashboard (WI 4130).
 *
 * No Properties tab (F2, handoff §18 / AC-18.4): an agency sells inventory belonging to the hotels
 * it contracts with, and those owners maintain the content in their own extranet (R9) — the
 * contract owns the agency's inventory, and the wizard is where a hotel gets imported. The
 * property routes bounce an agency home (hotelOnlyGuard). Settings keeps its place at the end.
 */
export const AGENCY_NAV: NavItem[] = [
  HOME,
  RATES,
  {
    label: 'nav.reservations', icon: 'event_available', route: '/extranet/bookings', area: 'RESERVATIONS',
    children: [
      RESERVATIONS_DASHBOARD,
      ALL_RESERVATIONS,
      { label: 'nav.children.reservationReports', icon: 'bar_chart', route: '/extranet/reports/reservations', area: 'REPORTS' },
    ],
  },
  { label: 'nav.clients', icon: 'contacts', route: '/extranet/clients', area: 'CLIENTS' },
  {
    label: 'nav.suppliers', icon: 'handshake', route: '/extranet/suppliers', area: 'SUPPLIERS',
    children: [
      { label: 'nav.children.allSuppliers', icon: 'handshake', route: '/extranet/suppliers', exact: true, area: 'SUPPLIERS' },
      { label: 'nav.children.buyVsSellReport', icon: 'bar_chart', route: '/extranet/reports/supplier-buy-sell', area: 'REPORTS' },
    ],
  },
  {
    label: 'nav.contracts', icon: 'history_edu', route: '/extranet/contracts', area: 'CONTRACTS',
    children: [
      { label: 'nav.children.allContracts', icon: 'history_edu', route: '/extranet/contracts', exact: true, area: 'CONTRACTS' },
      { label: 'nav.children.occupancyReport', icon: 'grid_on', route: '/extranet/reports/contract-occupancy', area: 'REPORTS' },
    ],
  },
  {
    label: 'nav.banking', icon: 'account_balance', route: '/extranet/banking', area: 'BANKING',
    children: [
      { label: 'nav.children.bankAccounts', icon: 'account_balance', route: '/extranet/banking', exact: true, area: 'BANKING' },
      { label: 'nav.children.companyExpenses', icon: 'receipt_long', route: '/extranet/banking/expenses', area: 'BANKING' },
    ],
  },
  SETTINGS,
];

// Matches the prototype's Shell.jsx ADMIN_NAV. Languages/AI Prompts shipped behind this same
// shell (multi-language Step 4) and are reachable from here, not only by typing their URL.
export const ADMIN_NAV: NavItem[] = [
  { label: 'nav.admin.propertyGroups', icon: 'groups', route: '/extranet/admin' },
  // Feature 4123: what each profile can see and change, right after Property groups.
  { label: 'nav.admin.profilePermissions', icon: 'admin_panel_settings', route: '/extranet/admin/profile-permissions' },
  { label: 'nav.admin.ratePlanCatalog', icon: 'receipt_long', route: '/extranet/admin/rate-plan-catalog' },
  { label: 'nav.admin.roomTypeCatalog', icon: 'bed', route: '/extranet/admin/room-type-catalog' },
  { label: 'nav.admin.languages', icon: 'language', route: '/extranet/admin/languages' },
  { label: 'nav.admin.aiPrompts', icon: 'auto_awesome', route: '/extranet/admin/ai-prompts' },
  // WI 3671: eZee KPIs of the environment, after AI Prompts (§4.A "Entry and route").
  { label: 'nav.admin.channelManager', icon: 'hub', route: '/extranet/admin/channel-manager' },
];

/** The tabs a role sees. An unknown/pending role gets the hotel set (the pre-F0 default). */
export function navItemsFor(role: ExtranetRole | null | undefined): NavItem[] {
  switch (role) {
    case 'ADMIN': return ADMIN_NAV;
    case 'AGENCY_MANAGER': return AGENCY_NAV;
    default: return HOTEL_NAV;
  }
}

/**
 * The tabs a member's profiles open (Feature 4123), for the shell's `navItems`: an item or a
 * submenu row of a NONE area is hidden (an agency member with Reports at None loses the report rows
 * under Reservations, Suppliers and Contracts); items and rows without an area always stay. A parent
 * whose own area is NONE but with at least one readable row stays, with only its readable rows,
 * and links to the first of them — only reachable after an admin override (e.g. Reservations NONE
 * + Reports READ). Pure: `levelOn` is the caller's level per area (`IdentityService.levelOn`).
 */
export function filterNav(items: readonly NavItem[], levelOn: (area: PageArea) => AccessLevel): NavItem[] {
  const readable = (area: PageArea | undefined) => !area || levelOn(area) !== 'NONE';
  const out: NavItem[] = [];
  for (const item of items) {
    const rows = item.children?.filter(c => readable(c.area));
    if (readable(item.area)) {
      out.push(!item.children || rows!.length === item.children.length ? item : withRows(item, rows!));
    } else if (rows?.length) {
      out.push({ ...withRows(item, rows), route: rows[0].route });
    }
  }
  return out;
}

/** The item with only `rows` under it (no `children` at all when none is left). */
function withRows(item: NavItem, rows: NavChild[]): NavItem {
  const copy: NavItem = { ...item, children: rows };
  if (!rows.length) delete copy.children;
  return copy;
}
